import { styled } from "@linaria/react";
import React from "react";
import type { ActiveAnalysis } from "../../../../../../../../../../api/useActiveAnalysesStream";
import type { Project } from "../../../../../../../../../../api/useProjects";
import type { Failure, Setup } from "../../../../../../../../../../api/useSetups";
import t from "../../../../../../../../../../i18n";
import validateInterface from "@ui/lib/validateInterface";
import useVisibleSeries from "@ui/Highcharts/hooks/useVisibleSeries";
import { HIDDEN_COLOR, Label, SquareSymbol } from "@ui/Highcharts/ui/Legend";
import ConfidenceIndicator from "@ui/Indicator/Confidence";
import { Divider } from "../../../../../../../../Chart/Legend/FailureLegend/Tooltip";
import { failureStatusDescription } from "../../../../../../../../Chart/series/getFailureSeries";
import * as io from "io-ts";
import { lightest } from "@ui";
import type {
  FailureValue,
  IndexedFailureValues,
} from "../../../../../../../../../../api/useIndexedFailureValues";
import type { IndexedManualFailureValues } from "../../../../../../../../../../api/useManualFailureValues";
import type { ManualFailure } from "../../../../../../../../../../api/useManualFailures";
import { BookmarkLine, StarFill } from "@ui/IconV2/react";
import type {
  FailureValueStatus,
  IndexedFailureMetadata,
} from "../../../../../../../../../../api/useFailureMetadata";
import { useSettings } from "../../../../../../../../../../api/useSettings";
import { QUANTITY_GAP_SERIE_ID } from "../../../../../../../../Diagnosis/Symptoms/getQuantityGapsSerie";
import useSeries from "../../../../../../../../Diagnosis/Symptoms/useSeries";
import { useFailureValuesMutation } from "../../../../../../../../Diagnosis/useFailureStatusMutation";
import QuantityChart from "./QuantityChart";
import FaultEventLegend from "../../../../../../../../Chart/Legend/FaultEventLegend";
import { getFailureDisplayName } from "../../../../../../../../Chart/Legend/failureLegendGrouping";
import { useFaultGraphOptions } from "../../FailureEvolution/hook/useFaultGraphOptions";
import { filterSeriesByEventOccurrence, filterSeriesByFocus } from "../../isFocusedFaultSeries";
import { useDisplayAllFaults } from "../../useDisplayAllFaults";
import { css } from "@linaria/core";
import { comparatorBy } from "diagnostics-lib/comparator";
import { withKeyNotNull } from "diagnostics-lib/typescript";
import type { Quantity } from "../../../../../../../../../../api/useQuantities";
import { colors, spacings } from "@ui/Theme";
import { isFaultPromoted } from "../../../../../../../../../../domain/isFaultPromoted";
import type { Event } from "../../../../../../../../../../api/useEvents";
import { getEventFaultNames, getEventScope } from "../../../../../../../../../../domain/event";
import { getEventVisibleImpactSeries } from "../../../../../../../utils/event";
import { QUANTITY_UNEXPLAINED_GAP_ID } from "../../../../../../../../Chart/series/serieIds";

const BorderedSquare = styled(SquareSymbol)<{ borderColor: string }>`
  border: 2px solid ${props => props.borderColor};
`;

function renderSymbol(serie: Highcharts.SeriesColumnOptions) {
  const color = serie.visible === false ? HIDDEN_COLOR : serie.color;
  const borderColor = serie.visible === false ? HIDDEN_COLOR : serie.borderColor;
  if (typeof color !== "string") throw new Error("color should be string");
  if (typeof borderColor !== "string") throw new Error("borderColor should be string");

  return <BorderedSquare color={color} borderColor={borderColor} />;
}

const SerieId = io.union([
  io.literal(QUANTITY_UNEXPLAINED_GAP_ID),
  io.literal(QUANTITY_GAP_SERIE_ID),
]);
type SerieId = io.TypeOf<typeof SerieId>;

function getOtherSerieDescription(serieId: SerieId) {
  switch (serieId) {
    case QUANTITY_UNEXPLAINED_GAP_ID:
      return t("diagnosis.symptoms.legend.tooltip.quantity_unexplained_gap");
    case QUANTITY_GAP_SERIE_ID:
      return t("diagnosis.symptoms.legend.tooltip.quantity_gap");
    default:
      throw new Error(serieId satisfies never);
  }
}

const ColorItemContent = styled.div<{ color: string; borderColor: string }>`
  width: 16px;
  height: 16px;
  background-color: ${props => props.color};
  border: 2px solid ${props => props.borderColor};
`;

function getRenderColorItemContentByFailureValue(
  failureValue: FailureValue | undefined,
  status: FailureValueStatus | undefined,
) {
  return function renderColorItemContent(color: string) {
    if (failureValue === undefined) return <></>;

    return (
      <ColorItemContent
        color={
          // failureValue.isManual || (!failureValue.isManual && failureValue.status === "CONFIRMED")
          status === "CONFIRMED" ? color : lightest(color)
        }
        borderColor={color}
      />
    );
  };
}

const statusToConfirme = new Set(["TO_BE_ANALYSED", "SUSPECTED", "REJECTED", "LEGACY_REJECTED"]);
const statusToSuspecte = new Set(["TO_BE_ANALYSED", "CONFIRMED", "REJECTED", "LEGACY_REJECTED"]);
const statusToReject = new Set(["TO_BE_ANALYSED", "SUSPECTED", "CONFIRMED"]);

interface P {
  visibleSymptomHistogramChartId: string;
  event: Event;
  project: Project;
  analysis: ActiveAnalysis;
  indexedFailureValues: IndexedFailureValues;
  indexedFailureMetadata: IndexedFailureMetadata;
  indexedManualFailureValues: IndexedManualFailureValues;
  observableChartRef: React.RefObject<Highcharts.Chart>;
  canUpdateFailureValues: boolean;
  quantities: Quantity[];
  setups: Setup[];
  setup: Setup;
}

const ProcessIndicators = ({
  visibleSymptomHistogramChartId,
  event,
  project,
  analysis,
  indexedFailureValues,
  indexedFailureMetadata,
  indexedManualFailureValues,
  quantities,
  observableChartRef,
  canUpdateFailureValues,
  setups,
  setup,
}: P) => {
  const faultEventScope = getEventScope(event);

  const [displayAllFaults, setDisplayAllFaults] = useDisplayAllFaults({
    scope: faultEventScope,
  });

  const { graphOptionMenu } = useFaultGraphOptions({
    event,
    quantities,
  });

  const settings = useSettings();
  const chartsRef = React.useRef<Map<string, React.RefObject<Highcharts.Chart>>>(new Map());

  const seriesFailureLegend = useSeries(
    project,
    analysis,
    indexedFailureMetadata,
    indexedFailureValues,
    indexedManualFailureValues,
    setup,
    quantities,
    false,
  );

  const quantityOfInterests = React.useMemo(
    () => quantities.filter(withKeyNotNull("qoiOrder")).sort(comparatorBy("qoiOrder")),
    [quantities],
  );
  const technicalNames = quantityOfInterests.map(qoi => qoi.name);
  for (const technicalName of technicalNames) {
    if (!chartsRef.current.has(technicalName))
      chartsRef.current.set(technicalName, React.createRef<Highcharts.Chart>());
  }
  for (const k of Array.from(chartsRef.current.keys())) {
    if (!technicalNames.includes(k)) chartsRef.current.delete(k);
  }
  const controlledChartRefs = technicalNames
    .slice(1)
    .map(technicalName => chartsRef.current.get(technicalName)!);

  const failureNames = getEventFaultNames(event);
  const series = React.useMemo(() => {
    const focusedSeries = filterSeriesByFocus({
      series: seriesFailureLegend,
      event,
      displayAllFaults,
      failureNames,
      getEventVisibleSeries: getEventVisibleImpactSeries,
    });
    return filterSeriesByEventOccurrence({
      series: focusedSeries,
      event,
      date: analysis.date,
      displayAllFaults,
    });
  }, [seriesFailureLegend, displayAllFaults, failureNames, event, analysis.date]);
  const [visibleSeriesFailureLegend, onSerieClickFailureLegend] = useVisibleSeries(
    visibleSymptomHistogramChartId,
    series,
  );

  const mutateFailureValues = useFailureValuesMutation(project);

  const renderFailureActions = React.useCallback(
    (failure: Failure | ManualFailure) => {
      if (!canUpdateFailureValues) return null;
      const failureValue = indexedFailureValues[analysis.date]?.[failure.name];

      if (!failureValue) return null;

      const bindFailureAction = (status: FailureValueStatus) => async (e: React.MouseEvent) => {
        e.stopPropagation();
        await mutateFailureValues(
          [
            {
              date: analysis.date,
              failureName: failureValue.failureName,
            },
          ],
          { status },
        );
      };

      const status = indexedFailureMetadata.getFailureStatus(
        failureValue.date,
        failureValue.failureName,
      );

      return (
        <div className={actions}>
          <div className={actionTitle}>{failureStatusDescription(status)}</div>
          <div className={actionTitle}>
            {t("diagnosis.chart.tooltip.subtitle_link.update_status")}
          </div>
          <div className={actionList}>
            {statusToConfirme.has(status) && (
              <div className={action} onClick={bindFailureAction("CONFIRMED")}>
                {t("diagnosis.chart.tooltip.button.confirmed")}
              </div>
            )}
            {statusToSuspecte.has(status) && (
              <div className={action} onClick={bindFailureAction("SUSPECTED")}>
                {t("diagnosis.chart.tooltip.button.suspected")}
              </div>
            )}
            {statusToReject.has(status) && (
              <div
                className={action}
                onClick={bindFailureAction(
                  settings.AUTOMATIC_DIAGNOSIS_RELAUNCH ? "REJECTED" : "LEGACY_REJECTED",
                )}
              >
                {t("diagnosis.chart.tooltip.button.false_detection")}
              </div>
            )}
          </div>
        </div>
      );
    },
    [
      canUpdateFailureValues,
      indexedFailureValues,
      mutateFailureValues,
      analysis.date,
      indexedFailureMetadata,
      settings.AUTOMATIC_DIAGNOSIS_RELAUNCH,
    ],
  );

  const renderLabel = React.useCallback(
    (failure: Failure | ManualFailure | undefined, serie: Highcharts.SeriesColumnOptions) => {
      const failureDisplayName = getFailureDisplayName(serie);

      if (!failure) return <Label>{failureDisplayName}</Label>;

      const failureValue = indexedFailureValues[analysis.date]?.[failure.name];
      const manualFailureValue = indexedManualFailureValues[analysis.date]?.[failure.name];

      const isPromotedFailure =
        "filters" in failure && isFaultPromoted(analysis.quantityValues, failure.filters);

      return (
        <StyledLabel>
          <div className={serieTitle}>{failureDisplayName}</div>
          <div className={labelIconWrapper}>
            {isPromotedFailure && <StarFill className={labelStarIcon} />}
            {failureValue && typeof failureValue?.confidence === "number" ? (
              <ConfidenceIndicator
                className={labelConfidenceIndicator}
                value={failureValue.confidence}
                size="small"
              />
            ) : null}
            {manualFailureValue && <BookmarkLine size="s" />}
          </div>
        </StyledLabel>
      );
    },
    [indexedFailureValues, analysis.date, analysis.quantityValues, indexedManualFailureValues],
  );

  const renderOtherSeriesTooltip = React.useCallback((serie: Highcharts.SeriesOptions) => {
    const serieId = validateInterface(SerieId, serie.id);

    return (
      <>
        <Divider />
        {getOtherSerieDescription(serieId)}
      </>
    );
  }, []);

  const getRenderColorItemContent = React.useCallback(
    (failure: Failure | ManualFailure) => {
      const failureValue = indexedFailureValues[analysis.date]?.[failure.name];

      const status = failureValue
        ? indexedFailureMetadata.getFailureStatus(failureValue.date, failureValue.failureName)
        : undefined;

      return getRenderColorItemContentByFailureValue(failureValue, status);
    },
    [analysis.date, indexedFailureValues, indexedFailureMetadata],
  );

  return (
    <>
      <FaultEventLegend
        project={project}
        setup={setup}
        chartRefs={[
          ...controlledChartRefs,
          observableChartRef,
          ...quantityOfInterests.map(qoi => chartsRef.current.get(qoi.name)!),
        ]}
        series={visibleSeriesFailureLegend}
        onLegendItemClick={onSerieClickFailureLegend}
        renderSymbol={renderSymbol}
        renderFailureActions={renderFailureActions}
        renderLabel={renderLabel}
        renderOtherSeriesTooltip={renderOtherSeriesTooltip}
        getRenderColorItemContent={getRenderColorItemContent}
        chartType={graphOptionMenu.graphType}
        displayAllFaults={displayAllFaults}
        onChangeDisplayAllFaults={() => setDisplayAllFaults(s => !s)}
      />
      <div className={quantityCharts}>
        {quantityOfInterests.map(qoi => (
          <QuantityChart
            visibleSymptomHistogramChartId={visibleSymptomHistogramChartId}
            key={qoi.name}
            qoi={qoi}
            project={project}
            event={event}
            failureNames={failureNames}
            setups={setups}
            analysis={analysis}
            indexedFailureMetadata={indexedFailureMetadata}
            indexedFailureValues={indexedFailureValues}
            indexedManualFailureValues={indexedManualFailureValues}
            chartRef={chartsRef.current.get(qoi.name)!}
            displayAllFaults={displayAllFaults}
          />
        ))}
      </div>
    </>
  );
};

export default React.memo(ProcessIndicators);

const quantityCharts = css`
  display: flex;
  flex-direction: row;
  overflow-x: auto;
  scroll-snap-type: x mandatory;

  > * {
    min-width: 150px;
    flex-shrink: 0;
  }
`;

const actions = css`
  margin-top: 8px;
`;

const actionTitle = css`
  font-weight: 600;
  margin-bottom: 8px;
`;

const actionList = css`
  font-weight: normal;
  display: flex;
  justify-content: space-around;
  gap: 16px;
`;

const action = css`
  text-decoration: underline;
  cursor: pointer;
`;

const StyledLabel = styled(Label)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 8px;
`;

const serieTitle = css`
  min-width: 0px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const labelIconWrapper = css`
  display: flex;
  align-items: center;
  gap: ${spacings.s};
  flex-shrink: 0;
  position: relative;
  padding-left: 10px;
`;

const labelStarIcon = css`
  color: ${colors.phosphorus400};
  width: 14px;
  height: 14px;
  position: absolute;
  top: -6px;
  left: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const labelConfidenceIndicator = css`
  margin-left: 5px;
`;
